import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router';

import { InsigniaEntorno } from '@/components/InsigniaEntorno';
import { SelectorTema } from '@/components/SelectorTema';
import { useStagger } from '@/lib/motion';

/**
 * Layout público (skill §2): composición a la izquierda (fotografía del doctor sobre la noche)
 * y el formulario en vidrio a la derecha. En móvil solo el formulario.
 */
export function AuthLayout({ children, titulo }: { children: ReactNode; titulo: string }) {
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-entra]', { step: 90 });
  return (
    <div ref={root} className="relative grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <SelectorTema className="absolute top-4 right-4 z-10 text-texto" />
      <aside className="relative hidden overflow-hidden lg:block" aria-hidden>
        <div className="bg-portada absolute inset-0 opacity-90" />
        <img
          src="/img/fondo-login-purple.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover mix-blend-soft-light"
        />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <img
            data-entra
            src="/img/consalud-horizontal-blanco.png"
            alt=""
            className="h-12 w-auto self-start"
          />
          <div data-entra className="relative z-10 max-w-[17rem] 2xl:max-w-[21rem]">
            <p className="text-4xl leading-tight font-extrabold">
              Acredita el ENARM, ¡a la primera!
            </p>
            <p className="mt-3 text-lg text-white/85">
              Clases, simulador, manuales y clases en vivo con enfoque 100&nbsp;% ENARM.
            </p>
          </div>
          <img
            data-entra
            src="/img/mascotas/doctor-foto.png"
            alt=""
            className="pointer-events-none absolute right-0 bottom-0 max-h-[62%] w-auto drop-shadow-2xl"
          />
        </div>
      </aside>
      <main className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            className="mb-8 flex justify-center lg:hidden"
            aria-label="Consalud ENARM"
          >
            <img
              src="/img/consalud-horizontal-blanco.png"
              alt="Consalud ENARM"
              className="solo-oscuro h-11"
            />
            <img
              src="/img/consalud-horizontal-color.png"
              alt="Consalud ENARM"
              className="solo-claro h-11"
            />
          </Link>
          <section data-entra className="card-vidrio p-6 sm:p-8">
            <img
              src="/img/isotipo-cerebro-blanco.png"
              alt=""
              className="solo-oscuro mx-auto mb-3 h-12"
            />
            <img
              src="/img/isotipo-cerebro-azul.png"
              alt=""
              className="solo-claro mx-auto mb-3 h-12"
            />
            <h1 className="mb-6 text-center text-2xl font-extrabold text-azul-texto">{titulo}</h1>
            {children}
          </section>
          <p data-entra className="mt-6 text-center text-xs text-texto-3">
            © {new Date().getFullYear()} Consalud ENARM ·{' '}
            <a href="/aviso-de-privacidad/" target="_blank" rel="noreferrer">
              Aviso de privacidad
            </a>
          </p>
        </div>
      </main>
      <InsigniaEntorno />
    </div>
  );
}

export function Campo({
  etiqueta,
  error,
  children,
}: {
  etiqueta: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-texto-2">{etiqueta}</span>
      {children}
      {error && (
        <span role="alert" className="mt-1 block text-xs font-semibold text-rojo-texto">
          {error}
        </span>
      )}
    </label>
  );
}

export function AvisoRecaptcha() {
  return (
    <p className="mt-4 text-center text-[11px] leading-snug text-texto-3">
      Este sitio está protegido por reCAPTCHA y aplican la{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
        Política de privacidad
      </a>{' '}
      y los{' '}
      <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
        Términos del servicio
      </a>{' '}
      de Google.
    </p>
  );
}
